import type React from 'react';
import { useEffect, useState } from 'react';
import { BroadcastThreadsEvent, useRoomOperations } from './RoomOperationsProvider';
import { useGlobalRoomOperations } from './GlobalRoomOperationsManager';
import type { ThreadWithOrigin } from './MergedThreadsProvider';
import { useRoom, useThreads, useEventListener } from '@liveblocks/react/suspense';
import type { Status } from '@liveblocks/client';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';

export enum RoomType {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

interface RoomRegistrarProps {
  roomType: RoomType;
  contentId: string;
  contentType: ContentType;
  onChange: (threads: ThreadWithOrigin[]) => void;
}

function ThreadsCollector({ origin, onChange }: { origin: RoomType; onChange: (threads: ThreadWithOrigin[]) => void }) {
  const { threads } = useThreads();

  useEffect(() => {
    if (threads?.length) {
      onChange(threads.map((t) => ({ ...t, __origin: origin })));
    } else {
      onChange([]);
    }
  }, [origin, onChange, threads]);
  return null;
}

export const RoomRegistrar: React.FC<RoomRegistrarProps> = ({ roomType, contentId, contentType, onChange }) => {
  const roomOperations = useRoomOperations();
  const { registerRoomOperations } = useGlobalRoomOperations();
  const { subscribe } = useRoom();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    registerRoomOperations(roomType, roomOperations);

    const unsubscribe = subscribe('status', (status) => {
      setStatus(status);
    });

    return () => {
      unsubscribe();
    };
  }, [roomType, roomOperations, registerRoomOperations, subscribe]);

  useEventListener(({ event }) => {
    if (!contentId || contentType !== ContentType.DOCUMENT || event === null) return;

    const typedEvent = event as { type?: string };
    if (typedEvent.type !== BroadcastThreadsEvent) return;

    const previewDocKey = QueryKeys.getDocumentMetadataByContentId(contentId);
    const previewGridsKey = QueryKeys.getGridsByContentId(contentId);

    queryClient.invalidateQueries({ queryKey: previewDocKey, exact: true });
    queryClient.invalidateQueries({ queryKey: previewGridsKey, exact: true });
  });

  if (status === 'connected') {
    return <ThreadsCollector origin={roomType} onChange={onChange} />;
  }

  return null;
};
